import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { onNotificationsRead } from '@/lib/unreadCountBus';

interface NotificationCenterProps {
  className?: string;
}

const getNotificationPreferences = (userId: string) => {
  const saved = localStorage.getItem(`notification_prefs_${userId}`);
  return saved
    ? JSON.parse(saved)
    : {
        mentions: true,
        replies: true,
        reactions: true,
        follows: true,
        sounds: true,
        browser_notifications: true,
      };
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({ className }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = async (userId: string) => {
    const { count } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    setUnreadCount(count || 0);
  };

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount(user.id);

    const channel = supabase
      .channel('notification-badge')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          fetchUnreadCount(user.id);

          const prefs = getNotificationPreferences(user.id);
          const notif: any = payload.new;
          if (
            prefs?.browser_notifications &&
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            const typeEnabled = prefs[notif.type as keyof typeof prefs];
            if (typeEnabled) {
              new Notification(notif.title, {
                body: notif.message,
                icon: '/vent-icon.png',
                tag: notif.id,
              });
            }
          }
          if (prefs?.sounds) {
            const audio = new Audio('/sounds/vent-notification.m4a');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => fetchUnreadCount(user.id),
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => fetchUnreadCount(user.id),
      )
      .subscribe();

    // Instant sync when the Notifications page marks notifications read
    const offBus = onNotificationsRead(({ all }) => {
      if (all) {
        setUnreadCount(0);
      } else {
        fetchUnreadCount(user.id);
      }
    });

    return () => {
      offBus();
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <div className={`relative ${className || ''}`}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate('/app/notifications')}
        className="relative"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>
    </div>
  );
};

export default NotificationCenter;
